import { io, type Socket } from 'socket.io-client';
import { create } from 'zustand';

import { apiGet, refreshAccessToken } from '@/lib/api';
import { useAuthStore, type ManagerRole } from '@/store/auth.store';

export type ChatAttachment =
  | { type: 'photo'; url: string; width: number | null; height: number | null }
  | { type: 'voice'; url: string; mime: string; durationMs: number; waveform: number[] };

/** Цитата в ответе: кто и что написал (текст сервер уже укоротил). */
export interface ChatReply {
  id: string;
  authorId: string | null;
  authorName: string;
  text: string;
  attachment: { type: 'photo'; url: string } | { type: 'voice'; durationMs: number } | null;
  deleted: boolean;
}

export interface ChatMessage {
  id: string;
  /** У фото и голосовых — подпись или пусто. */
  text: string;
  createdAt: string;
  author: { id: string | null; name: string; role: ManagerRole; avatarUrl?: string | null };
  attachment?: ChatAttachment | null;
  /** Удалено автором у всех: в ленте серая плашка «Сообщение удалено». */
  deleted?: boolean;
  replyTo?: ChatReply | null;
}

/** Удалено у всех: на месте сообщения — плашка, в цитатах ответов на него — тоже. */
export function markDeleted(messages: ChatMessage[], id: string): ChatMessage[] {
  return messages.map((m) => {
    if (m.id === id) return { ...m, text: '', attachment: null, replyTo: null, deleted: true };
    if (m.replyTo?.id === id) return { ...m, replyTo: { ...m.replyTo, text: '', attachment: null, deleted: true } };
    return m;
  });
}

interface ChatState {
  unread: number;
  setUnread: (n: number) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  unread: 0,
  setUnread: (unread) => set({ unread }),
}));

const SOCKET_URL = import.meta.env.VITE_API_URL?.trim() || window.location.origin;
const MAX_AUTH_RETRIES = 3;

let socket: Socket | null = null;
let chatOpen = false;
let authRetries = 0;
const messageListeners = new Set<(m: ChatMessage) => void>();
const deleteListeners = new Set<(id: string) => void>();
const hideListeners = new Set<(id: string) => void>();

export async function refreshUnread(): Promise<void> {
  try {
    const { unread } = await apiGet<{ unread: number }>('/chat/unread', { silent: true });
    useChatStore.getState().setUnread(unread);
  } catch {
    /* следующая попытка — при переподключении сокета */
  }
}

function chatVisible(): boolean {
  return chatOpen && document.visibilityState === 'visible';
}

/** Один сокет на весь кабинет: счётчик в меню и лента чата слушают одно соединение. */
export function connectChat(): void {
  if (socket) return;
  // Только websocket, как в меню: за nginx несколько web-инстансов без sticky-сессий,
  // long-polling между ними терял бы сессию.
  const s = io(SOCKET_URL, {
    path: '/socket.io',
    transports: ['websocket'],
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 5_000,
    auth: (cb) => cb({ token: useAuthStore.getState().accessToken ?? '', kind: 'manager' }),
  });
  socket = s;

  s.on('connect', () => {
    authRetries = 0;
    if (chatVisible()) s.emit('sales-chat:presence', { open: true });
    void refreshUnread();
  });

  // После отказа middleware socket.io сам не переподключается: access истёк —
  // обновляем сессию и пробуем снова, но не бесконечно.
  s.on('connect_error', () => {
    if (s.active || authRetries >= MAX_AUTH_RETRIES) return;
    authRetries += 1;
    void refreshAccessToken().then((token) => {
      if (token && socket === s) window.setTimeout(() => s.connect(), 1000 * authRetries);
    });
  });

  s.on('sales-chat:message', (m: ChatMessage) => {
    const me = useAuthStore.getState().manager?.id;
    if (m.author.id !== me && !chatVisible()) {
      const { unread, setUnread } = useChatStore.getState();
      setUnread(unread + 1);
    }
    messageListeners.forEach((fn) => fn(m));
  });

  s.on('sales-chat:delete', (p: { id: string }) => {
    deleteListeners.forEach((fn) => fn(p.id));
    // Удалённое не считается непрочитанным — счётчик в меню пересчитает сервер.
    if (!chatVisible()) void refreshUnread();
  });

  // «Удалить у меня» из другой вкладки этого же менеджера.
  s.on('sales-chat:hide', (p: { id: string }) => {
    hideListeners.forEach((fn) => fn(p.id));
  });
}

export function disconnectChat(): void {
  socket?.disconnect();
  socket = null;
  chatOpen = false;
  useChatStore.getState().setUnread(0);
}

/** Чат открыт и виден — сервер не шлёт этому менеджеру пуш. */
export function setChatPresence(open: boolean): void {
  chatOpen = open;
  socket?.emit('sales-chat:presence', { open: open && document.visibilityState === 'visible' });
}

export function onChatMessage(fn: (m: ChatMessage) => void): () => void {
  messageListeners.add(fn);
  return () => messageListeners.delete(fn);
}

export function onChatDelete(fn: (id: string) => void): () => void {
  deleteListeners.add(fn);
  return () => deleteListeners.delete(fn);
}

export function onChatHide(fn: (id: string) => void): () => void {
  hideListeners.add(fn);
  return () => hideListeners.delete(fn);
}
